import {
  Box,
  Card,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { SectionTitle } from '~/components/common/SectionTitle';

import { formatCurrency } from './contractDetailShared';
import type { ContractReview } from './types';

type Props = {
  contract: ContractReview;
};

export const ContractRatesTab = ({ contract }: Props) => {
  const { rateSets, properties } = contract;
  const { currencyCode } = useContext(UserContext);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Grid container spacing={3}>
        {rateSets.length > 0 ? (
          rateSets.map((rateSet) => {
            // Properties that point at this rate set via their rateSetId.
            const assignedProperties = properties.filter((p) => p.rateSetId === rateSet.id);

            return (
              <Grid key={rateSet.id} size={{ xs: 12 }}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <SectionTitle>{rateSet.name}</SectionTitle>
                  <Typography variant="caption" color="text.secondary">
                    {rateSet.description}
                  </Typography>

                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {assignedProperties.length > 0 ? (
                      assignedProperties.map((p) => (
                        <Chip key={p.id} size="small" label={p.name} variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Not assigned to any property.
                      </Typography>
                    )}
                  </Box>

                  <TableContainer sx={{ mt: 1.5 }}>
                    <Table size="small" sx={{ minWidth: 560 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Worker</TableCell>
                          <TableCell>Work</TableCell>
                          <TableCell>Rate type</TableCell>
                          <TableCell align="right">Hourly</TableCell>
                          <TableCell align="right">OT mult.</TableCell>
                          <TableCell>Coverage</TableCell>
                          <TableCell>Default</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rateSet.rates.map((rate, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {rate.workerType}
                              {rate.description ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', textTransform: 'none' }}
                                >
                                  {rate.description}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {rate.workType}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {rate.rateType}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency({ value: rate.hourlyRate, currencyCode })}
                            </TableCell>
                            <TableCell align="right">
                              {rate.overtimeMultiplier !== null
                                ? `${rate.overtimeMultiplier}x`
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {rate.coverageType.replace('-', ' ')}
                            </TableCell>
                            <TableCell>
                              {rate.isDefault ? (
                                <Chip size="small" label="Default" variant="outlined" />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Grid size={{ xs: 12 }}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <SectionTitle>Rate Sets</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No rate set on file for this contract.
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
